import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDateAU } from '../../../shared/format';
import { missingToClose, rolledReviewDue, type ReviewAction, type ReviewStep } from '../../../shared/reviewCase';

interface ReviewCaseView {
  id: string;
  openedAt: string;
  dueDate: string | null;
  packReadyAt: string | null;
  meetingDate: string | null;
  actions: ReviewAction[];
  noActions: boolean;
  roaNotRequired: string | null;
  closedAt: string | null;
  nextReviewDue: string | null;
  steps: ReviewStep[];
  stage: string;
}

const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Brisbane' }).format(new Date());

/**
 * The annual review as a case: pack, meeting, notes, actions, RoA, roll the
 * date. Notes and RoA tick themselves from the record; the rest is set here.
 */
export function ReviewCasePanel({ clientId, canEdit, onOpenTab }: { clientId: string; canEdit: boolean; onOpenTab?: (tab: string) => void }) {
  const qc = useQueryClient();
  const key = ['client', clientId, 'reviews'];
  const q = useQuery({ queryKey: key, queryFn: () => api<{ data: ReviewCaseView[] }>(`/api/clients/${clientId}/reviews`) });
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (q.isLoading || !q.data) return null;
  const cases = q.data.data;
  const open = cases.find((c) => !c.closedAt) ?? null;
  const lastClosed = cases.find((c) => c.closedAt) ?? null;

  async function call(path: string, init: RequestInit, done?: string) {
    setBusy(true); setMsg(null);
    try {
      await api(`/api/clients/${clientId}/reviews${path}`, init);
      await Promise.all([qc.invalidateQueries({ queryKey: key }), qc.invalidateQueries({ queryKey: ['dashboard'] }), qc.invalidateQueries({ queryKey: ['client', clientId, 'full'] })]);
      if (done) setMsg(done);
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Save failed'); } finally { setBusy(false); }
  }
  const patch = (body: object, done?: string) => call(`/${open!.id}`, { method: 'PATCH', body: JSON.stringify(body) }, done);
  async function sendTask(actionId: string) {
    setBusy(true); setMsg(null);
    try {
      const r = await api<{ sent: boolean; reason: string | null }>(`/api/clients/${clientId}/reviews/${open!.id}/actions/${actionId}/task`, { method: 'POST', body: '{}' });
      await qc.invalidateQueries({ queryKey: key });
      setMsg(r.sent ? 'Sent to Next-Task.' : `Not sent to Next-Task${r.reason === 'not-configured' ? ': the Next-Task link is not set up on this server' : r.reason ? ` (${r.reason})` : ''}.`);
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Send failed'); } finally { setBusy(false); }
  }

  if (!open) {
    return (
      <section className="qa-card p-5 mb-6 qa-no-print" aria-label="Review case" data-testid="review-case">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="qa-eyebrow">Annual review</p>
            <p className="text-xs text-ink-mute mt-1">
              {lastClosed ? `Last review closed ${formatDateAU(lastClosed.closedAt!.slice(0, 10))}${lastClosed.nextReviewDue ? `, next due ${formatDateAU(lastClosed.nextReviewDue)}` : ''}.` : 'No review on record yet.'}
            </p>
          </div>
          {canEdit && <button type="button" className="btn-primary text-sm" disabled={busy} onClick={() => call('', { method: 'POST', body: '{}' }, 'Review opened.')}>Start review</button>}
        </div>
        {msg && <p role="status" className="text-xs text-ink-soft mt-2">{msg}</p>}
      </section>
    );
  }

  const missing = missingToClose(open.steps);
  return (
    <section className="qa-card p-5 mb-6 qa-no-print" aria-label="Review case" data-testid="review-case">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
        <p className="qa-eyebrow">Annual review in progress</p>
        <p className="text-xs text-ink-mute">Opened {formatDateAU(open.openedAt.slice(0, 10))}{open.dueDate ? ` · due ${formatDateAU(open.dueDate)}` : ''} · <span className="qa-mono">{open.stage}</span></p>
      </div>
      <ol className="space-y-3">
        {open.steps.map((s, i) => (
          <li key={s.key} className="flex flex-wrap items-start gap-3" data-testid={`review-step-${s.key}`}>
            <span aria-hidden className={`mt-0.5 w-5 h-5 shrink-0 rounded-full border text-[11px] flex items-center justify-center ${s.done ? 'bg-forest text-paper border-forest' : 'border-hairline-paper text-ink-mute'}`}>{s.done ? '✓' : i + 1}</span>
            <div className="flex-1 min-w-[12rem]">
              <p className="text-sm font-medium">{s.label}<span className="sr-only">{s.done ? ' (done)' : ' (to do)'}</span></p>
              {s.detail && <p className="text-xs text-ink-mute">{/^\d{4}-\d{2}-\d{2}$/.test(s.detail) ? formatDateAU(s.detail) : s.detail}</p>}
              {canEdit && <StepControl step={s} c={open} busy={busy} patch={patch} sendTask={sendTask} onOpenTab={onOpenTab} />}
            </div>
          </li>
        ))}
      </ol>
      {canEdit && <CloseControl c={open} missing={missing} busy={busy}
        onClose={(next) => call(`/${open.id}/close`, { method: 'POST', body: JSON.stringify({ nextReviewDue: next }) }, `Review closed. Next review ${formatDateAU(next)}.`)}
        onCancel={() => call(`/${open.id}`, { method: 'DELETE' }, 'Review cancelled.')} />}
      {msg && <p role="status" className="text-xs text-ink-soft mt-3">{msg}</p>}
    </section>
  );
}

function StepControl({ step, c, busy, patch, sendTask, onOpenTab }: {
  step: ReviewStep; c: ReviewCaseView; busy: boolean; patch: (body: object, done?: string) => void; sendTask: (actionId: string) => void; onOpenTab?: (tab: string) => void;
}) {
  const [reason, setReason] = useState(c.roaNotRequired ?? '');
  switch (step.key) {
    case 'pack':
      return (
        <label className="text-xs inline-flex items-center gap-2 mt-1">
          <input type="checkbox" checked={!!c.packReadyAt} disabled={busy} onChange={(e) => patch({ packReady: e.target.checked })} />
          Brief below reviewed and pack ready
        </label>
      );
    case 'meeting':
      return (
        <label className="text-xs flex items-center gap-2 mt-1 whitespace-nowrap">Meeting date
          <input type="date" className="qa-input text-sm max-w-[12rem]" aria-label="Meeting date" value={c.meetingDate ?? ''} disabled={busy}
            onChange={(e) => patch({ meetingDate: e.target.value || null })} />
        </label>
      );
    case 'notes':
      return step.done ? null : (
        <p className="text-xs text-ink-soft mt-1">
          Ticks itself when a meeting file note is saved.{' '}
          {onOpenTab && <button type="button" className="text-rust hover:underline" onClick={() => onOpenTab('notes')}>Add the meeting note</button>}
        </p>
      );
    case 'actions':
      return <ActionsEditor c={c} busy={busy} patch={patch} sendTask={sendTask} />;
    case 'roa':
      return step.done && !c.roaNotRequired ? null : (
        <div className="text-xs mt-1 space-y-1">
          {!step.done && <p className="text-ink-soft">Ticks itself when a Record of Advice is created{onOpenTab && <>{' '}(<button type="button" className="text-rust hover:underline" onClick={() => onOpenTab('advice')}>Strategy tab</button>)</>}, or record why none is needed.</p>}
          <div className="flex gap-2 items-center">
            <input className="qa-input text-sm flex-1" aria-label="Why no RoA is needed" placeholder="e.g. No change to advice" value={reason} onChange={(e) => setReason(e.target.value)} />
            <button type="button" className="btn-ghost text-xs" disabled={busy || reason.trim() === (c.roaNotRequired ?? '')}
              onClick={() => patch({ roaNotRequired: reason.trim() || null })}>{reason.trim() || !c.roaNotRequired ? 'Save reason' : 'Clear'}</button>
          </div>
        </div>
      );
    default:
      return null;
  }
}

function ActionsEditor({ c, busy, patch, sendTask }: { c: ReviewCaseView; busy: boolean; patch: (body: object, done?: string) => void; sendTask: (actionId: string) => void }) {
  const [text, setText] = useState('');
  const [owner, setOwner] = useState('Adviser');
  const [due, setDue] = useState('');
  const save = (actions: ReviewAction[]) => patch({ actions });
  return (
    <div className="mt-1 space-y-2">
      {c.actions.length > 0 && (
        <ul className="space-y-1">
          {c.actions.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center gap-2 text-xs">
              <input type="checkbox" checked={a.done} disabled={busy} aria-label={`Done: ${a.text}`}
                onChange={(e) => save(c.actions.map((x) => (x.id === a.id ? { ...x, done: e.target.checked } : x)))} />
              <span className={a.done ? 'line-through text-ink-mute' : ''}>{a.text}</span>
              <span className="text-ink-mute">· {a.owner}{a.due ? ` · by ${formatDateAU(a.due)}` : ''}</span>
              {a.taskSentAt
                ? <span className="ml-auto text-forest">In Next-Task</span>
                : <button type="button" className="ml-auto text-rust hover:underline" disabled={busy} aria-label={`Send to Next-Task: ${a.text}`} onClick={() => sendTask(a.id)}>Send to Next-Task</button>}
              <button type="button" className="text-rust hover:underline" disabled={busy} aria-label={`Remove action: ${a.text}`}
                onClick={() => save(c.actions.filter((x) => x.id !== a.id))}>Remove</button>
            </li>
          ))}
        </ul>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_8rem_10rem_auto] gap-2 items-center">
        <input className="qa-input text-sm" aria-label="New action" placeholder="Action agreed in the meeting" value={text} onChange={(e) => setText(e.target.value)} />
        <input className="qa-input text-sm" aria-label="Action owner" placeholder="Owner" value={owner} onChange={(e) => setOwner(e.target.value)} />
        <input type="date" className="qa-input text-sm" aria-label="Action due" value={due} onChange={(e) => setDue(e.target.value)} />
        <button type="button" className="btn-ghost text-xs" disabled={busy || !text.trim()}
          onClick={() => { save([...c.actions, { id: '', text: text.trim(), owner: owner.trim() || 'Adviser', due: due || null, done: false }]); setText(''); setDue(''); }}>
          Add action
        </button>
      </div>
      {c.actions.length === 0 && (
        <label className="text-xs inline-flex items-center gap-2">
          <input type="checkbox" checked={c.noActions} disabled={busy} onChange={(e) => patch({ noActions: e.target.checked })} />
          No actions came out of this review
        </label>
      )}
    </div>
  );
}

function CloseControl({ c, missing, busy, onClose, onCancel }: {
  c: ReviewCaseView; missing: ReviewStep[]; busy: boolean; onClose: (next: string) => void; onCancel: () => void;
}) {
  const suggested = rolledReviewDue(c.meetingDate, today());
  const [next, setNext] = useState(suggested);
  return (
    <div className="mt-5 pt-4 border-t border-hairline-paper flex flex-wrap items-end gap-3">
      <label className="text-xs">Next review
        <input type="date" className="qa-input block mt-1 text-sm max-w-[12rem]" aria-label="Next review date" min={today()} value={next} onChange={(e) => setNext(e.target.value)} />
      </label>
      <button type="button" className="btn-primary text-sm" disabled={busy || missing.length > 0 || !next} onClick={() => onClose(next)}>
        Close review and roll the date
      </button>
      <button type="button" className="btn-ghost text-xs ml-auto" disabled={busy} onClick={onCancel}>Cancel review</button>
      {missing.length > 0 && <p className="basis-full text-[11px] text-ink-mute">To close: {missing.map((s) => s.label).join(', ')}.</p>}
    </div>
  );
}
